import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  InputGroup,
  FormControl,
  Button,
  Table,
  Form
} from 'react-bootstrap';
import Flex from 'components/common/Flex';
import IngredientsMgmtModal from 'components/pages/haccp/inspStandardInfo/ingredientsMgmt/modal/ingredientsMgmtModal';
import { useConfirmModalState } from 'global/confirmModalState';

// types
import { ACTION_TYPE } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
// api
import {
  inspIrdntCreate,
  inspIrdntDetail,
  inspIrdntUpdate,
  inspLabCodeCheck
} from 'api/haccp/inspStandardInfo/ingredientsMgmtApi';
import { commonCode } from 'api/haccp/commonApi';

/**
 * @description 검사성분관리 상세페이지
 */

const Detail = () => {
  /* router info*/
  const history = useHistory();
  const { state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  const [modalShow, setModalShow] = useState(false);
  const [action, setAction] = useState('');
  const [actionType, setActionType] = useState('');
  const [validated, setValidated] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [ingredientOptions, setIngredientOptions] = useState([]);

  // 검사성분 정보
  const [irdntInfo, setIrdntInfo] = useState({
    inspLabCd: '', // 의뢰기관코드
    inspIrdntKindCd: '', // 검사성분유형코드
    inspIrdntCd: '', // 검사성분코드
    labInspIrdntCd: '', // 검사결과서코드
    isResultCodeCheck: null, // 검사결과서코드 중복체크 (null: 중복체크안함, false: 중복, true: 사용가능)
    inspIrdntNm: '', // 검사성분명
    cmplxInspIrdntYn: 'N', // 복합검사성분여부
    useYn: 'Y', // 사용유무
    isDisabled: false
  });

  // 복합검사성분 정보
  const [irdntInfoList, setIrdntInfoList] = useState([]);

  // 복합검사성분정보 원본(기존 복합검사성분 정보 등록 여부 Check)
  const [irdntInfoListOrigin, setIrdntInfoListOrigin] = useState([]);

  /* event */

  /* 목록페이지 이동 */
  const onList = () => {
    history.push({
      pathname: '/haccp/inspectionSetting',
      state: state
    });
  };

  /* 검사결과서 코드 중복확인 */
  const duplicateCheck = async () => {
    if (irdntInfo.inspLabCd === '' || irdntInfo.inspIrdntCd === '') {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '의뢰기관 및 검사성분 코드를 입력해주세요.',
        isConfirmBtn: true
      });

      setIrdntInfo({
        ...irdntInfo,
        labInspIrdntCd: '',
        isResultCodeCheck: null
      });

      return;
    }

    const requestBody = {
      inspLabCd: irdntInfo.inspLabCd,
      inspIrdntCd: irdntInfo.inspIrdntCd,
      labInspIrdntCd: irdntInfo.labInspIrdntCd,
      inspIrdntKindCd: irdntInfo.inspIrdntKindCd
    };

    const response = await inspLabCodeCheck(requestBody);

    const { code, data } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      return;
    }

    const { duplicateYn, labInspIrdntCd } = data || {};

    if (duplicateYn === 'Y') {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '이미 등록된  검사결과서 코드 입니다.',
        isConfirmBtn: true
      });

      setIrdntInfo({ ...irdntInfo, isResultCodeCheck: false });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '사용가능한 검사결과서 코드 입니다.',
      isConfirmBtn: true
    });

    setIrdntInfo({
      ...irdntInfo,
      labInspIrdntCd: labInspIrdntCd,
      isResultCodeCheck: true
    });
  };

  /* 등록 or 수정 */
  const handleSubmit = event => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    const { isResultCodeCheck } = irdntInfo || {};

    /**
     * isResultCodeCheck
     * 중복코드 미확인: null
     * 중복코드인 경우 : false
     * 코드 사용가능: true
     */
    if (isResultCodeCheck === null || isResultCodeCheck === false) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '검사결과서코드 중복확인을 해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    // 복합검사성분 정보 미등록 예외처리
    if (irdntInfo.cmplxInspIrdntYn === 'Y' && irdntInfoList.length === 0) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '복합검사성분 정보를 등록해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `검사성분관리 정보를 ${action}하시겠습니까?`,
      isCancelBtn: true,
      isConfirmBtn: true,
      onConfirm: () => {
        onSubmit();
      }
    });
  };

  const onSubmit = async () => {
    const {
      inspLabCd,
      inspIrdntKindCd,
      inspIrdntCd,
      labInspIrdntCd,
      inspIrdntNm,
      cmplxInspIrdntYn,
      useYn
    } = irdntInfo || {};

    let requestBody = {};
    let response = {};

    if (actionType === ACTION_TYPE.CREATE) {
      // 등록
      requestBody = {
        inspLabCd: inspLabCd,
        inspIrdntKindCd: inspIrdntKindCd,
        inspIrdntCd: inspIrdntCd,
        labInspIrdntCd: labInspIrdntCd,
        inspIrdntNm: inspIrdntNm,
        cmplxInspIrdntYn: cmplxInspIrdntYn,
        useYn: useYn,
        elmntInspIrdntMapList: irdntInfoList?.map(e => ({
          elmntInspIrdntSeq: e.seq
        }))
      };

      response = await inspIrdntCreate(requestBody);
    } else {
      // 수정
      requestBody = {
        inspLabCd: inspLabCd,
        inspIrdntKindCd: inspIrdntKindCd,
        inspIrdntCd: inspIrdntCd,
        labInspIrdntCd: labInspIrdntCd,
        inspIrdntNm: inspIrdntNm,
        cmplxInspIrdntYn: cmplxInspIrdntYn,
        useYn: useYn,
        elmntInspIrdntMapList: irdntInfoList?.map(e => ({
          seq: e.histType === 'C' ? null : e.seq,
          cmplxInspIrdntSeq: e.cmplxInspIrdntSeq,
          elmntInspIrdntSeq: e.elmntInspIrdntSeq,
          histType: e.histType,
          delYn: 'N'
        }))
      };

      response = await inspIrdntUpdate(seq, requestBody);
    }

    const { code, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `검사성분관리정보가 정상 ${action} 되었습니다.`,
      isConfirmBtn: true
    });

    // 등록일 경우만 목록페이지 조회조건 초기화 및 sort조건 변경
    if (actionType === ACTION_TYPE.CREATE) {
      history.push({
        pathname: '/haccp/inspectionSetting',
        state: {
          ...state,
          inspLabCd: '',
          inspIrdntKindCd: '',
          useYn: '',
          divisionKey: '',
          divisionValue: '',
          page: 0,
          sort: 'seq,desc'
        }
      });
      return;
    }

    onList();
  };

  /* 검사성분등록 Modal 선택완료 */
  const modalConfirm = value => {
    // 기존선택 된 복합검사 성분 정보 및 Modal에서 추가한 검사성분
    const array = [...value, ...irdntInfoList?.filter(e => e.isCheck === true)];

    // 복합검사성분정보 Data 중복제거
    const duplicateArray = array?.reduce(function (acc, current) {
      if (
        acc.findIndex(
          ({ elmntInspIrdntSeq }) =>
            elmntInspIrdntSeq === current.elmntInspIrdntSeq
        ) === -1
      ) {
        acc.push(current);
      }
      return acc;
    }, []);

    setIrdntInfoList(
      duplicateArray?.map(e => {
        // 신규등록된 검사성분목록
        e.histType = 'C';
        e.cmplxInspIrdntSeq = Number(seq);
        e.elmntInspIrdntSeq = e.seq;
        irdntInfoListOrigin?.forEach(data => {
          // 기존 검사성분목록
          if (e.elmntInspIrdntSeq === data.elmntInspIrdntSeq) {
            e.histType = 'U';
            e.seq = data.seq;
            e.cmplxInspIrdntSeq = data.cmplxInspIrdntSeq;
            e.elmntInspIrdntSeq = data.elmntInspIrdntSeq;
          }
        });
        return e;
      })
    );

    setModalShow(false);
  };

  /* 상세 API */
  const onDetail = async () => {
    const response = await inspIrdntDetail(seq);

    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const {
      inspLabCd,
      inspIrdntKindCd,
      inspIrdntCd,
      labInspIrdntCd,
      inspIrdntNm,
      cmplxInspIrdntYn,
      useYn,
      elmntInspIrdntMapList
    } = data || {};

    // 검사성분관리
    setIrdntInfo({
      ...irdntInfo,
      inspLabCd: inspLabCd, // 의뢰기관코드
      inspIrdntKindCd: inspIrdntKindCd, // 검사성분유형코드
      inspIrdntCd: inspIrdntCd, // 검사성분코드
      labInspIrdntCd: labInspIrdntCd, // 검사결과서코드
      isResultCodeCheck: true, // 검사결과서코드 중복체크
      inspIrdntNm: inspIrdntNm, // 검사성분명
      cmplxInspIrdntYn: cmplxInspIrdntYn, // 복합검사성분여부
      useYn: useYn, // 사용유무
      isDisabled: true
    });

    // 복합검사성분 정보 목록
    setIrdntInfoList(
      elmntInspIrdntMapList?.map(e => ({
        isCheck: true,
        ...e
      })) ?? []
    );

    // 복합검사성분 정보 목록 원본Data
    setIrdntInfoListOrigin(elmntInspIrdntMapList);
  };

  /* 의뢰기관목록 API */
  const requestAgency = async () => {
    const params = {
      codeGroup: 'sfsh_insp_lab',
      useYn: 'Y',
      sort: 'orders,asc'
    };
    const response = await commonCode(params);

    const { code, list = [] } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setAgencyOptions(list);
  };

  /* 검사성분유형 API */
  const ingredientList = async () => {
    const params = {
      codeGroup: 'sfsh_irdnt_kind',
      useYn: 'Y',
      sort: 'orders,asc'
    };
    const response = await commonCode(params);

    const { code, list = [] } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setIngredientOptions(list);
  };

  /* watch */
  useEffect(() => {
    // 의뢰기관 API 호출
    requestAgency();
    // 검사성분 유형 API 호출
    ingredientList();
  }, []);

  useEffect(() => {
    switch (actionType) {
      case ACTION_TYPE.CREATE: {
        setAction('등록');
        break;
      }
      case ACTION_TYPE.UPDATE: {
        setAction('수정');
        onDetail();
        break;
      }
    }
  }, [actionType]);

  useLayoutEffect(() => {
    if (seq) {
      setActionType(ACTION_TYPE.UPDATE);
      return;
    }
    setActionType(ACTION_TYPE.CREATE);
  }, []);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">
                  분석 항목 관리: 검사 성분명, 분석처 유형 설정
                </h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="bg-light">
            <Row className="g-3 mb-3">
              {/* 의뢰기관 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*의뢰기관</Form.Label>
                  <Form.Select
                    required
                    size="sm"
                    onChange={e =>
                      setIrdntInfo({ ...irdntInfo, inspLabCd: e.target.value })
                    }
                    value={irdntInfo.inspLabCd}
                    disabled={irdntInfo.isDisabled}
                  >
                    <option hidden value="">
                      의뢰기관 선택
                    </option>
                    {agencyOptions?.map(option => (
                      <option key={option.codeCd} value={option.codeCd}>
                        {option.codeNmKr}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* 검사성분유형 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*검사성분유형</Form.Label>
                  <Form.Select
                    required
                    size="sm"
                    onChange={e =>
                      setIrdntInfo({
                        ...irdntInfo,
                        inspIrdntKindCd: e.target.value
                      })
                    }
                    value={irdntInfo.inspIrdntKindCd}
                    disabled={irdntInfo.isDisabled}
                  >
                    <option hidden value="">
                      검사성분유형 선택
                    </option>
                    {ingredientOptions?.map(option => (
                      <option key={option.codeCd} value={option.codeCd}>
                        {option.codeNmKr}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              {/* 검사성분 코드 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>
                    *검사성분 코드(SFSH 시스템에서 사용할 코드)
                  </Form.Label>
                  <FormControl
                    required
                    type="text"
                    placeholder="20자 이내로 입력해주세요."
                    size="sm"
                    maxLength="20"
                    value={irdntInfo.inspIrdntCd}
                    onChange={e =>
                      setIrdntInfo({
                        ...irdntInfo,
                        inspIrdntCd: e.target.value
                      })
                    }
                    disabled={irdntInfo.isDisabled}
                  />
                </Form.Group>
              </Col>
              {/* 검사결과서 코드 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>
                    *검사결과서 코드(각 의뢰기관별 검사결과서의 검사 성분별
                    코드)
                  </Form.Label>
                  <InputGroup>
                    <FormControl
                      required
                      type="text"
                      placeholder="20자 이내로 입력해주세요."
                      size="sm"
                      maxLength="20"
                      value={irdntInfo.labInspIrdntCd}
                      onChange={e =>
                        setIrdntInfo({
                          ...irdntInfo,
                          labInspIrdntCd: e.target.value,
                          isResultCodeCheck: null
                        })
                      }
                      disabled={irdntInfo.isDisabled}
                    />
                    {irdntInfo.isDisabled === false && (
                      <Button
                        variant="success"
                        size="sm"
                        name="labInspIrdntCd"
                        disabled={irdntInfo.labInspIrdntCd === ''}
                        onClick={duplicateCheck}
                      >
                        중복확인
                      </Button>
                    )}
                    {irdntInfo.isDisabled === false &&
                      irdntInfo.isResultCodeCheck === true && (
                        <div className="text-primary d-flex align-items-center ms-2 fw-bold">
                          사용가능
                        </div>
                      )}
                    {irdntInfo.isDisabled === false &&
                      irdntInfo.isResultCodeCheck === false && (
                        <div className="text-danger d-flex align-items-center ms-2 fw-bold">
                          중복코드
                        </div>
                      )}
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              {/* 검사상분명 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*검사성분명(실제 분석명)</Form.Label>
                  <FormControl
                    required
                    type="text"
                    placeholder="20자 이내로 입력해주세요."
                    size="sm"
                    maxLength="20"
                    value={irdntInfo.inspIrdntNm}
                    onChange={e =>
                      setIrdntInfo({
                        ...irdntInfo,
                        inspIrdntNm: e.target.value
                      })
                    }
                    disabled={irdntInfo.isDisabled}
                  />
                </Form.Group>
              </Col>
              {/* 복합 검사성분 여부 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>
                    *복합 검사성분 여부(Met +Cys 같이 둘의 함량을 기준으로
                    설정할 경우만 Y임)
                  </Form.Label>
                  <Flex>
                    <Form.Check
                      type="radio"
                      id="use"
                      label="Y"
                      name="cmplxInspIrdntYn"
                      value="Y"
                      checked={irdntInfo.cmplxInspIrdntYn === 'Y'}
                      onChange={e =>
                        setIrdntInfo({
                          ...irdntInfo,
                          cmplxInspIrdntYn: e.target.value
                        })
                      }
                      disabled={irdntInfo.isDisabled}
                    />
                    <Form.Check
                      type="radio"
                      id="unuse"
                      label="N"
                      className="ms-3"
                      name="cmplxInspIrdntYn"
                      value="N"
                      checked={irdntInfo.cmplxInspIrdntYn === 'N'}
                      onChange={e =>
                        setIrdntInfo({
                          ...irdntInfo,
                          cmplxInspIrdntYn: e.target.value
                        })
                      }
                      disabled={irdntInfo.isDisabled}
                    />
                  </Flex>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              {/* 사용유무 */}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*사용유무</Form.Label>
                  <Flex>
                    <Form.Check
                      type="radio"
                      id="use"
                      label="사용"
                      name="useYn"
                      value="Y"
                      checked={irdntInfo.useYn === 'Y'}
                      onChange={e =>
                        setIrdntInfo({
                          ...irdntInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="unuse"
                      label="미사용"
                      className="ms-3"
                      name="useYn"
                      value="N"
                      checked={irdntInfo.useYn === 'N'}
                      onChange={e =>
                        setIrdntInfo({
                          ...irdntInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                  </Flex>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
          {irdntInfo.cmplxInspIrdntYn === 'N' && (
            <Card.Footer className="d-flex align-items-center">
              <Button
                size="sm"
                variant="secondary"
                className="ms-auto"
                onClick={onList}
              >
                취소
              </Button>
              <Button size="sm" className="ms-2" type="submit">
                {action}
              </Button>
            </Card.Footer>
          )}
        </Card>
        {/* 복합검사성분 정보 */}
        {irdntInfo.cmplxInspIrdntYn === 'Y' && (
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col>
                  <h5 className="mb-0">복합검사성분 정보</h5>
                </Col>
                <Col xs={'auto'}>
                  <Button
                    size="sm"
                    className="ms-2"
                    onClick={() => setModalShow(true)}
                  >
                    검사성분 등록
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="pt-0">
              <Table responsive bordered className="fs--1 text-center mb-0">
                <thead className="bg-200 text-700 align-middle border-300">
                  <tr>
                    <th>No.</th>
                    <th>의뢰기관</th>
                    <th>검사성분 코드</th>
                    <th>검사결과서 코드</th>
                    <th>검사성분</th>
                    <th>검사성분유형</th>
                  </tr>
                </thead>
                <tbody>
                  {irdntInfoList?.map(
                    (
                      {
                        inspLabNm,
                        inspIrdntCd,
                        labInspIrdntCd,
                        inspIrdntNm,
                        inspIrdntKindNm
                      },
                      index
                    ) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{inspLabNm}</td>
                        <td>{inspIrdntCd}</td>
                        <td>{labInspIrdntCd}</td>
                        <td>{inspIrdntNm}</td>
                        <td>{inspIrdntKindNm}</td>
                      </tr>
                    )
                  )}
                  {irdntInfoList?.length < 1 && (
                    <tr>
                      <td colSpan={6} className="w-auto">
                        복합검사성분 정보가 존재하지 않습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>

            <Card.Footer className="d-flex align-items-center">
              <Button
                size="sm"
                variant="secondary"
                className="ms-auto"
                onClick={onList}
              >
                취소
              </Button>
              <Button size="sm" className="ms-2" type="submit">
                {action}
              </Button>
            </Card.Footer>
          </Card>
        )}
      </Form>
      {modalShow === true && (
        <IngredientsMgmtModal
          modalShow={modalShow}
          setModalShow={setModalShow}
          modalConfirm={modalConfirm}
          itemList={irdntInfoList}
          setItemList={setIrdntInfoList}
        ></IngredientsMgmtModal>
      )}
    </>
  );
};

export default Detail;
